<template>
  <div class="main-container">
    <div class="main-form">
      <v-card>
        <v-card-title>
          <h2 class="mb-0">Create New Arena</h2>
        </v-card-title>
        <v-card-text>
          <Loader v-if="IsDataLoadInProgress"></Loader>

          <div v-else class="form">
        <div class="form__row">
          <label class="form__label" for="arena-create-partner">Partner (not required)</label>
          <div class="form__input-box">
            <select id="arena-create-partner" class="form__select" v-model="partnerId">
              <option value="null">----------</option>
              <option v-for="partner in partners" :value="partner.id" :key="partner.id">{{ partner.name }}</option>
            </select>
          </div>
        </div>
        <div class="form__row" v-if="false">
          <label class="form__label" for="arena-create-parent">Parent Arena (not required)</label>
            <div class="form__input-box">
              <select id="arena-create-parent" class="form__select" v-model="parentArenaId">
                <option value="null">----------</option>
                <option v-for="arena in arenas" :value="arena.id" :key="arena.id">{{ arena.name }}</option>
              </select>
            </div>
        </div>
        <div class="form__row">
          <label class="form__label" for="arena-create-domain">Arena domain (required)</label>

          <div class="multiple-inputs-wrap">
            <div class="form__input-box form__input-box--small">
              <select id="arena-create-schema" class="form__select" v-model="chosenSchema" aria-label="Protocol">
                <option value="https">https</option>
              </select>
            </div>

            <div class="form__input-box">
              <input
                id="arena-create-domain"
                type="text"
                :class="{'form__input': true, 'is-error': errors.has('domain')}"
                v-model="arenaDomainFormatted"
                v-validate="'required'"
                name="domain"
                v-input-trim
                data-testid="arena-domain-input"
              >
            </div>
          </div>

          <span class="form__error" v-show="errors.has('domain')">{{ errors.first('domain') }}</span>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-name">Arena name (required)</label>
            <input
              id="arena-create-name"
              type="text"
              :class="{'form__input': true, 'is-error': errors.has('name')}"
              v-model="arenaName"
              v-validate="'required'"
              name="name"
              v-input-trim
              data-testid="arena-name-input"
            >

            <span class="form__error" v-show="errors.has('name')">{{ errors.first('name') }}</span>
          </div>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-locale">Default locale (required)</label>
            <select id="arena-create-locale" class="form__select" v-model="arenaLocale" data-testid="arena-locale-select">
              <option :key="loc.value" :value="loc.value" v-for="(loc) in locales">{{loc.name}}</option>
            </select>
          </div>
        </div>

        <div class="form__row form__row--feed">
          <span class="form__label" id="arena-create-feed-label">Feed</span>

          <div class="multiple-inputs-wrap">
            <div class="form__input-box">
              <v-autocomplete outlined
                        dense
                        hide-details
                        id="arena-create-feed"
                        aria-labelledby="arena-create-feed-label"
                        v-model="selectedFeedId"
                        :items="feeds"
                        :item-text="feedOptionText"
                        item-value="id"
                        data-testid="arena-feed-select">
              </v-autocomplete>
            </div>
            <div class="form__input-box form__input-box--feed-action">
              <v-btn
                dense
                :href="selectedFeedHref"
                target="_blank"
                rel="noopener noreferrer"
                :disabled="!canOpenSelectedFeed"
                data-testid="arena-open-feed-btn"
              >
                Open feed
              </v-btn>
            </div>
          </div>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-ga">Google Analytics</label>
            <input
              id="arena-create-ga"
              type="text"
              class="form__input"
              v-model="gaKey"
              name="analytics"
              v-input-trim
              data-testid="arena-ga-input"
            >
          </div>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-facebook">Facebook Application ID - use for user login on arena</label>
            <input
                    id="arena-create-facebook"
                    type="text"
                    class="form__input"
                    v-model="facebookAppId"
                    name="facebookAppId"
                    v-input-trim
                    data-testid="arena-facebook-input"
            >
          </div>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-favicon">Upload favicon, it must be named <span class="form__important-text">favicon.png</span> (required)</label>

            <input
              id="arena-create-favicon"
              type="file"
              :class="{'form__input': true, 'is-error': errors.has('favicon')}"
              v-validate="faviconRules"
              ref="faviconInput"
              name="favicon"
              @change="processFile($event, 'favicon')"
              data-testid="arena-favicon-input"
            >

            <span class="form__error" v-show="errors.has('favicon')">{{ errors.first('favicon') }}</span>
          </div>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-logo">Upload logo, it must be named <span class="form__important-text">logo.png</span> (required)</label>

            <input
              id="arena-create-logo"
              type="file"
              :class="{'form__input': true, 'is-error': errors.has('logo')}"
              v-validate="logoRules"
              ref="logoInput"
              name="logo"
              @change="processFile($event, 'logo')"
              data-testid="arena-logo-input"
            >
            <span class="form__error" v-show="errors.has('logo')">{{ errors.first('logo') }}</span>
          </div>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-homepage">Client home page url for logo link (optional)</label>
            <input
              id="arena-create-homepage"
              type="text"
              class="form__input"
              v-model="clientHomePageUrl"
              name="clientHomePageUrl"
              v-input-trim
              data-testid="arena-homepage-input"
            >
          </div>
        </div>

        <div class="form__row">
          <div class="form__input-box">
            <label class="form__label" for="arena-create-crm">CRM Contract Name (optional)</label>
            <input
              id="arena-create-crm"
              type="text"
              class="form__input"
              v-model="crmName"
              name="crmName"
              v-input-trim
            >
          </div>
        </div>

        <div class="form__row" v-if="createArenaError">
          <span class="creation-edition-error">{{createArenaError}}</span>
        </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="$checkRoles(['Administrator'])"
            color="success"
            :loading="isLoadingBtn"
            @click="createArena"
            data-testid="arena-create-btn"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <InfoModal
      v-if="arenaIsCreated"
      :msg="'Arena was successfully created.'"
      :onCloseModal="closeInfoModal"
    ></InfoModal>
  </div>
</template>

<script>
import config from '../../../config';
import Loader from '../../Loader/Loader.vue';
import InfoModal from '../../InfoModal/InfoModal.vue';

export default {
    components: { Loader, InfoModal },
    name: 'CreateForm',
    props: ['getCreatedArenas', 'locales', 'feeds', 'sortings', 'IsDataLoadInProgress', 'arenas', 'partners'],
    data() {
        return {
            chosenSchema: 'https',
            parentArenaId: null,
            partnerId: null,
            arenaDomain: '',
            createdDomain: '', // it is used for sitemap generation
            arenaName: '',
            arenaLocale: 'en',
            selectedFeedId: 0,
            chosenFeed: 'default',
            // promoGame: null,
            clientHomePageUrl: '',
            gaKey: '',
            facebookAppId: '',
            crmName: '',
            createArenaError: '',
            isLoadingBtn: false,
            arenaIsCreated: false,
            favicon: null,
            logo: null
        };
    },
    computed: {
        canOpenSelectedFeed() {
            return Number(this.selectedFeedId) > 0;
        },
        selectedFeedHref() {
            return this.canOpenSelectedFeed ? `/feeds/${this.selectedFeedId}` : undefined;
        },
        faviconRules() {
            return {
                image: true,
                mimes: 'image/png',
                fileNameCheck: 'favicon.png', // checking file name with this string
                required: true
            };
        },
        logoRules() {
            return {
                image: true,
                mimes: 'image/png',
                fileNameCheck: 'logo.png', // checking file name with this string
                required: true
            };
        },
        arenaDomainFormatted: {
            get: function() {
                return this.arenaDomain;
            },
            set: function(val) {
                this.arenaDomain = val.toLowerCase().trim();
            }
        }
    },
    methods: {
        feedOptionText(item) {
            if (!item) {
                return '';
            }
            return `#${item.id} Name: ${item.name} Type: ${item.environment}`;
        },
        processFile: function(e, file) {
            this[file] = e.target.files[0]; // adding file to this.logo or this.favicon
        },
        createArena: function() {
            this.$validator.validateAll().then((isValid) => {
                if (isValid) {
                    this.isLoadingBtn = true;

                    const formData = new FormData();
                    const domainOrSubDomain = this.arenaDomain.indexOf('.') === -1 ? 'SubDomain' : 'Domain';
                    const textData = JSON.stringify({
                        parentArenaId: this.parentArenaId,
                        partnerId: this.partnerId,
                        schema: this.chosenSchema,
                        [domainOrSubDomain]: this.arenaDomain,
                        arenaName: this.arenaName,
                        locale: this.arenaLocale,
                        ArenaFeedId: this.selectedFeedId,
                        ArenaFeed: 'default',
                        // 'PromoGame': this.promoGame,
                        GA: this.gaKey,
                        FacebookAppId: this.facebookAppId,
                        // 'CrmContractName': this.crmName,
                        ClientHomePageUrl: this.clientHomePageUrl
                    });

                    formData.append('JsonData', textData);
                    formData.append('logo', this.logo);
                    formData.append('favicon', this.favicon);

                    this.$http.post(`${config.apiUrl}/arena/create`, formData).then(
                        async (response) => {
                            this.createdDomain = response.body.url;
                            
                            // Create domain in game-api
                            try {
                                await this.$gameApi.post('/domains', {
                                    name: this.arenaDomain,
                                    feedId: this.selectedFeedId
                                });
                                console.log('Domain created successfully in game-api');
                            } catch (domainError) {
                                console.error('Error creating domain in game-api:', domainError);
                                // Continue with sitemap creation even if domain creation fails
                            }
                            
                            const model = [this.createdDomain];
                            const options = {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            };
                            this.$http.post(`${config.apiUrl}/sitemap/create`, model, options).then(
                                (response) => {
                                    this.isLoadingBtn = false;
                                    this.arenaIsCreated = true;
                                },
                                (error) => {
                                    console.log(error, 'sitemap creation error');
                                    this.isLoadingBtn = false;
                                    this.createArenaError =
                                        'Something went wrong with creating sitemap, for more info see browser console';
                                }
                            );

                            // resetting form after creation success
                            this.parentArenaId = '';
                            this.chosenSchema = 'http';
                            this.arenaDomain = '';
                            this.arenaName = '';
                            this.arenaLocale = 'en';
                            this.chosenFeed = 'default';
                            // this.promoGame = ''
                            this.clientHomePageUrl = '';
                            this.gaKey = '';
                            this.facebookAppId = '';
                            this.createArenaError = '';
                            this.$refs.logoInput.value = '';
                            this.$refs.faviconInput.value = '';
                            this.$validator.reset();
                        },
                        (error) => {
                            console.log(error, 'arena creation error');
                            this.createArenaError = error.body.Message;
                            this.isLoadingBtn = false;
                        }
                    );
                }
            });
        },
        closeInfoModal: function() {
            this.arenaIsCreated = false;
            this.$emit('arena-created');
        }
    }
};
</script>

<style scoped src="./CreateForm.scss" lang="scss">
</style>
